import { observable, action, computed } from 'mobx';
import {
    IMember,
    ISignatureBlockResponse,
    ISignatureVariableValue
} from '~/types/server.types';
import type { RootStore } from '~/stores/root.store';
import { BaseBlock } from '~/models/blocks/base-block.model';
import { ISignature, SignatureTypes } from '~/types/model.types';
import type { FlowRevision } from '~/models/flow-revision.model';
import { Variable } from '~/models/variable.model';

export type SignatureBlockDataForServer = ReturnType<
    BaseBlock['getDataForServer']
> &
    Pick<ISignatureBlockResponse, 'type' | 'signature'>;

export class SignatureBlock extends BaseBlock {
    type: 'signature_block';
    @observable signature: ISignature;

    constructor(
        block: ISignatureBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.connected_variable_id = block.connected_variable_id;
        this.signature = block.signature || {
            signee_id: '',
            signee_name: '',
            signee_email: '',
            signee_type: 'client',
            required: false,
            signee_details: null
        };
    }

    get isDeletable() {
        return true;
    }

    @computed
    get isSigned() {
        return !!this.connectedVariable?.value?.signature;
    }

    isDraggable() {
        return false;
    }

    dndAcceptedTypes() {
        return null;
    }

    defaultSignatureData(): Partial<ISignatureVariableValue> {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;

        // we only prefill name for vendor
        if (this.signature.signee_type === 'client') {
            return null;
        }

        const signee = selectedFlow.flowVendorsList.find(
            vendor => vendor._id === this.signature.signee_id
        );

        return {
            signature: signee?.full_name,
            signature_type: SignatureTypes.TYPE
        };
    }

    get connectedVariable(): Variable<ISignatureVariableValue> {
        return super.connectedVariable;
    }

    @computed
    get shouldExpandOnSelect() {
        return false;
    }

    @computed
    get isAssigned() {
        return !!this.signature.signee_id;
    }

    @computed
    get signeeType() {
        return this.signature.signee_type;
    }

    @computed
    get signeeName() {
        return this.signature.signee_name;
    }

    @computed
    get signeeDetails() {
        return this.signature.signee_details;
    }

    @computed
    get signeeEmail() {
        return this.signature.signee_email;
    }

    @computed
    get isRequired() {
        return this.signature.required;
    }

    @computed
    get errors() {
        const {
            builderUiStore: { showErrors, selectedFlow }
        } = this.rootStore;

        const recipients = selectedFlow?.flowRecipients || [];
        const vendors = selectedFlow?.flowVendorsList || [];
        const collaborators = selectedFlow?.flowCollaboratorList || [];

        if (selectedFlow.is_template || !showErrors) {
            return {};
        }

        const errors: any = {};

        if (!this.isAssigned) {
            errors.hasErrors = true;
        }

        if (
            this.signature.signee_type === 'vendor' &&
            !vendors
                .concat(collaborators)
                .some(vendor => vendor._id === this.signature.signee_id)
        ) {
            errors.hasErrors = true;
        }
        if (
            this.signature.signee_type === 'client' &&
            !recipients.some(
                recipient => recipient._id === this.signature.signee_id
            )
        ) {
            errors.hasErrors = true;
        }

        return errors;
    }

    @action
    setSignee(signee: Partial<IMember>) {
        this.signature.signee_id = signee?._id;
        this.signature.signee_name = signee?.full_name;
        this.signature.signee_email = signee?.email;
    }

    getDataForServer(): SignatureBlockDataForServer {
        return {
            ...super.getDataForServer(),
            signature: { ...this.signature },
            type: this.type as 'signature_block'
        };
    }

    @action
    mergeBlockAttributes(block, attributes) {
        super.mergeBlockAttributes(block, attributes);
        if (
            'connected_variable_id' in attributes &&
            block.connected_variable_id
        ) {
            this.connected_variable_id = block.connected_variable_id;
        }
    }
}
